import { DateTime } from 'luxon'

import {
  formatWithZoneLabel,
  instantInZone,
} from '../../ts-utils/luxon-permanent-dst'

/** Weekday-prefixed date, e.g. 'Wed, Aug 19, 2026'. */
const DATE = 'ddd, MMM D, YYYY'
const TIME = 'h:mm A'
const DATE_TIME = `${DATE} ${TIME}`

/**
 * The zone a value is rendered in: the one the caller named (the event's own
 * authored timezone), or the VIEWER's when it named none.
 *
 * Naming the viewer's zone explicitly, rather than letting `DateTime.fromISO`
 * fall back to it, is what puts the permanent-DST correction on that path too.
 * A reader in British Columbia carries the same stale timezone database as
 * anyone else, so their own local time needs correcting exactly as a venue's
 * does — `useTimezoneManager` treats `userDate` this way for the schedule row,
 * and this file was the remaining path that did not.
 *
 * Null only on a runtime that cannot name its own zone, where callers fall
 * back to luxon's untouched behaviour.
 */
const displayZone = (zone?: string | null): string | null =>
  zone || DateTime.local().zoneName

/**
 * Parses a stored UTC ISO string into the zone it should be read in, following
 * the same convention as the game list (`useTimezoneManager`'s `userDate` /
 * `GameListRowTime`'s `getDateAndGameTime`), with the `ZZZZ` format token
 * rendering the short zone abbreviation (e.g. "PDT", "EST") for that zone.
 */
const toLocal = (
  iso: string | null | undefined,
  zone?: string | null
): DateTime | null => {
  if (!iso) {
    return null
  }
  //  Via `instantInZone` so a zone that abolished its clock change is read on
  //  the offset it actually keeps, whatever the viewer's timezone database
  //  still says. See ts-utils/luxon-permanent-dst.
  const target = displayZone(zone)
  const dt = target ? instantInZone(iso, target) : DateTime.fromISO(iso)
  return dt.isValid ? dt : null
}

/**
 * The zone abbreviation to show for a value, with the label its residents use
 * put back when the zone was substituted for its offset. See
 * ts-utils/luxon-permanent-dst.
 */
const zoneLabel = (dt: DateTime, zone?: string | null): string => {
  const target = displayZone(zone)
  return target ? formatWithZoneLabel(dt, target, 'ZZZZ') : dt.toFormat('ZZZZ')
}

/** Formats a Luxon DateTime the same way `dayjs(...).format(DATE)` etc. would. */
const fmt = (dt: DateTime, pattern: string): string => {
  // Translate the dayjs-style tokens used elsewhere in this file into Luxon
  // tokens, keeping the public output identical to the previous dayjs-based
  // implementation.
  const luxonPattern = pattern
    .replace('YYYY', 'yyyy')
    .replace('MMM D', 'MMM d')
    .replace('h:mm A', 'h:mm a')
    // Luxon spells the abbreviated weekday 'EEE'; dayjs's 'ddd' would render
    // literally as the letters "ddd".
    .replace('ddd', 'EEE')
  return dt.toFormat(luxonPattern)
}

/**
 * Human-readable event date range, converted from a stored UTC ISO string
 * into the VIEWER's browser-local timezone before formatting, with the local
 * zone abbreviation appended.
 * - same day  → "Sep 12, 2026 · 2:00 PM – 6:00 PM (PDT)"
 * - multi-day → "Sep 12, 2026 2:00 PM – Sep 14, 2026 6:00 PM (PDT)"
 * - no end    → "Sep 12, 2026 · 2:00 PM (PDT)"
 * Returns '' when the start date is missing or unparseable.
 *
 * Same-day vs multi-day is decided on the local-zone-converted values.
 *
 * `timezone` is OPTIONAL. When provided (e.g. the event's own authored IANA
 * zone), the range is rendered in THAT zone instead of the runtime's local
 * zone, with that zone's abbreviation. Omitting it preserves the previous
 * behaviour exactly (runtime/browser zone) — existing callers are unaffected.
 */
export const formatEventDateRange = (
  startsAt: string,
  endsAt: string | null,
  timezone?: string | null
): string => {
  const start = toLocal(startsAt, timezone)
  if (!start) {
    return ''
  }

  const zone = zoneLabel(start, timezone)
  const end = toLocal(endsAt, timezone)

  if (!end) {
    return `${fmt(start, DATE)} · ${fmt(start, TIME)} (${zone})`
  }

  if (start.hasSame(end, 'day')) {
    return `${fmt(start, DATE)} · ${fmt(start, TIME)} – ${fmt(
      end,
      TIME
    )} (${zone})`
  }

  return `${fmt(start, DATE_TIME)} – ${fmt(end, DATE_TIME)} (${zone})`
}

/**
 * The same range as `formatEventDateRange`, split into renderable parts.
 *
 * A multi-day event returns TWO lines so a UI can stack them — one date per
 * line reads far better than a single string wrapping mid-range. Same-day and
 * no-end events return exactly one line, so callers can render the array
 * uniformly without branching on the case.
 *
 * The zone abbreviation is returned separately rather than baked into the
 * last line, so callers may show it once (or style it apart) instead of
 * repeating it per line.
 */
export interface EventDateRangeParts {
  /** One entry for a same-day/no-end event; two for a multi-day one. */
  lines: string[]
  /** Local zone abbreviation, e.g. 'PDT'. '' when the start is unparseable. */
  zone: string
  /** True when start and end fall on different calendar days. */
  isMultiDay: boolean
  /**
   * The two ends as separately addressable values, for a caller that wants to
   * label them ("Start" / "End") and hang a second line off each rather than
   * render `lines` as a plain stack.
   *
   * Only populated for a MULTI-DAY event — that is the only case where the two
   * ends are far enough apart to be worth splitting. A same-day or open-ended
   * event leaves this null and callers fall back to `lines`.
   */
  endpoints: EventDateRangeEndpoints | null
}

export interface EventDateRangeEndpoints {
  /** e.g. 'Tue, Aug 25, 2026 5:12 PM' — no trailing dash, unlike `lines`. */
  start: string
  end: string
}

const EMPTY_PARTS: EventDateRangeParts = {
  lines: [],
  zone: '',
  isMultiDay: false,
  endpoints: null,
}

export const formatEventDateRangeParts = (
  startsAt: string,
  endsAt: string | null,
  timezone?: string | null
): EventDateRangeParts => {
  const start = toLocal(startsAt, timezone)
  if (!start) {
    return EMPTY_PARTS
  }

  const zone = zoneLabel(start, timezone)
  const end = toLocal(endsAt, timezone)

  if (!end) {
    return {
      lines: [`${fmt(start, DATE)} · ${fmt(start, TIME)}`],
      zone,
      isMultiDay: false,
      endpoints: null,
    }
  }

  if (start.hasSame(end, 'day')) {
    return {
      lines: [`${fmt(start, DATE)} · ${fmt(start, TIME)} – ${fmt(end, TIME)}`],
      zone,
      isMultiDay: false,
      endpoints: null,
    }
  }

  // Multi-day: one labelled line per end, so neither date is buried mid-string.
  return {
    lines: [`${fmt(start, DATE_TIME)} –`, fmt(end, DATE_TIME)],
    zone,
    isMultiDay: true,
    // Same two values without the joining dash, for the labelled Start/End
    // layout the detail page uses.
    endpoints: {
      start: fmt(start, DATE_TIME),
      end: fmt(end, DATE_TIME),
    },
  }
}
